import { useWorkbook } from '@/context/WorkbookContext';
import { WorkbookPage, SectionTitle, Paragraph, WorksheetInput, AffirmationBox } from '@/components/WorkbookPage';
import { PenLine, Heart } from 'lucide-react';

export const CommitmentPage = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.capstone || {};
  
  const updateData = (field, value) => {
    updateFormData('capstone', { ...data, [field]: value });
  };
  
  const today = new Date().toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
  
  return (
    <WorkbookPage pageId="commitment" prevPage="life-money-map" nextPage="section-6-divider">
      <header className="mb-10 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <PenLine className="h-8 w-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Personal Commitment Statement
          </h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">
          A promise to yourself—written in your own words
        </p>
      </header>
      
      <Paragraph>
        This is the most important page in the workbook. Everything you've learned, everything 
        you've planned—it means nothing without action. This commitment statement is your 
        promise to yourself. Make it personal. Make it real.
      </Paragraph>
      
      <div className="my-12 bg-card border-2 border-primary/20 rounded-2xl p-8 md:p-12 relative">
        {/* Decorative corner */}
        <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-primary/30" />
        <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-primary/30" />
        <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-primary/30" />
        <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-primary/30" />
        
        <div className="text-center mb-8">
          <h2 className="font-serif text-2xl font-bold mb-2">My Commitment</h2>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Heart className="h-4 w-4 text-primary" />
            <span className="text-sm italic">A promise to my future self</span>
          </div>
        </div>
        
        <div className="space-y-8">
          <div>
            <label className="block text-sm font-medium mb-3 text-center">
              I, <span className="border-b border-primary">{formData.userName || '_____________'}</span>, 
              commit to the following:
            </label>
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">
              I understand that my financial well-being is my responsibility. I commit to:
            </p>
            <textarea
              className="w-full min-h-[120px] border-2 border-dashed border-border rounded-lg p-4 bg-muted/30 focus:outline-none focus:border-primary text-center"
              placeholder="Write your specific commitments... (e.g., 'Track my spending every week', 'Save 10% of every paycheck', 'Review my budget monthly')"
              value={data.specificCommitments || ''}
              onChange={(e) => updateData('specificCommitments', e.target.value)}
            />
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">
              When I face challenges, I will remember:
            </p>
            <textarea
              className="w-full min-h-[80px] border-2 border-dashed border-border rounded-lg p-4 bg-muted/30 focus:outline-none focus:border-primary text-center"
              placeholder="What will keep you going when it gets hard?"
              value={data.motivation || ''}
              onChange={(e) => updateData('motivation', e.target.value)}
            />
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">
              My "why" — the deeper reason I'm doing this:
            </p>
            <textarea
              className="w-full min-h-[80px] border-2 border-dashed border-border rounded-lg p-4 bg-muted/30 focus:outline-none focus:border-primary text-center"
              placeholder="For my family, for my peace of mind, for my future..."
              value={data.why || ''}
              onChange={(e) => updateData('why', e.target.value)}
            />
          </div>
          
          <div className="pt-8 border-t border-border">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-3">Signature (type your name):</p>
                <input
                  type="text"
                  className="w-full border-b-2 border-primary bg-transparent py-2 text-center text-lg font-serif focus:outline-none"
                  placeholder="Your name"
                  value={data.signature || ''}
                  onChange={(e) => updateData('signature', e.target.value)}
                />
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-3">Date:</p>
                <p className="text-lg font-serif py-2 border-b-2 border-primary/30">{today}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <SectionTitle className="text-center">What Happens Next</SectionTitle>
      
      <div className="max-w-xl mx-auto space-y-4 text-center">
        <Paragraph>
          This isn't the end—it's the beginning. Your commitment is a living document. 
          Return to it when you need motivation. Update it as you grow.
        </Paragraph>
        
        <div className="grid gap-4 my-8">
          <div className="bg-secondary/50 rounded-lg p-4">
            <p className="font-semibold">Weekly:</p>
            <p className="text-sm text-muted-foreground">Review your spending and progress</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-4">
            <p className="font-semibold">Monthly:</p>
            <p className="text-sm text-muted-foreground">Update your budget, celebrate wins</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-4">
            <p className="font-semibold">Quarterly:</p>
            <p className="text-sm text-muted-foreground">Review 90-day goals, set new ones</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-4">
            <p className="font-semibold">Yearly:</p>
            <p className="text-sm text-muted-foreground">Review this entire workbook, update your map</p>
          </div>
        </div>
      </div>
      
      <AffirmationBox>
        I am committed to my financial well-being. Every small step counts. 
        I am capable, I am worthy, and I am on my way.
      </AffirmationBox>
      
      <div className="text-center mt-12 p-6 bg-primary/5 rounded-xl">
        <p className="font-serif text-lg italic text-foreground">
          "The best time to plant a tree was 20 years ago. The second best time is now."
        </p>
        <p className="text-sm text-muted-foreground mt-2">— Chinese Proverb</p>
      </div>
    </WorkbookPage>
  );
};
